import React, { useEffect, useRef, useState } from 'react';
import './Slide01Hero.css';

export default function Slide01Hero({ slideNumber, totalSlides, pdfMode }) {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(pdfMode?8:0);

  // Canvas: 3D rings + orbs + icosahedron + lightning
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    let cx = W / 2, cy = H / 2;
    let running = true;

    const handleResize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      cx = W / 2; cy = H / 2;
    };
    window.addEventListener('resize', handleResize);

    const lts = []; const bursts = [];
    function spawnLightning() {
      const a = Math.random() * 6.28;
      const d = Math.min(W, H) * 0.06;
      const sx = cx + Math.cos(a) * d, sy = cy * 0.4 + Math.sin(a) * d;
      const len = Math.min(W, H) * (0.07 + Math.random() * 0.1);
      const pts = [{ x: sx, y: sy }];
      let px = sx, py = sy;
      for (let i = 0; i < 6 + Math.floor(Math.random() * 5); i++) {
        px += Math.cos(a) * (len / 9) + (Math.random() - 0.5) * 24;
        py += Math.sin(a) * (len / 9) + (Math.random() - 0.5) * 24;
        pts.push({ x: px, y: py });
      }
      lts.push({ pts, life: 0, mx: 10 + Math.random() * 8, c: Math.random() > 0.3 ? '165,42,58' : '251,191,36' });
    }
    function spawnBurst(bx, by, n, c) {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.28, sp = 1.5 + Math.random() * 5;
        bursts.push({ x: bx, y: by, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0, mx: 25 + Math.random() * 30, r: Math.random() * 2 + 0.3, c });
      }
    }

    // Entrance bursts
    setTimeout(() => spawnBurst(cx, cy * 0.4, 80, '165,42,58'), 300);
    setTimeout(() => spawnBurst(cx, cy * 0.4, 50, '165,42,58'), 400);
    setTimeout(() => spawnBurst(cx, cy * 0.7, 40, '251,191,36'), 2200);
    setTimeout(() => spawnBurst(cx, cy * 0.78, 45, '165,42,58'), 3000);

    let T = 0;
    function draw() {
      if (!running) return;
      T++;
      ctx.clearRect(0, 0, W, H);
      const sc = Math.min(W, H), ly = cy * 0.4;

      // 3D Rings
      const rings = [
        { r: sc * 0.22, tilt: 0.3, speed: 0.00035, w: 1.4, c: '165,42,58', a: 0.05 },
        { r: sc * 0.18, tilt: -0.22, speed: -0.00055, w: 0.9, c: '251,191,36', a: 0.035, dash: [5, 10] },
        { r: sc * 0.14, tilt: 0.5, speed: 0.00045, w: 1.1, c: '122,21,48', a: 0.04, dash: [3, 7] },
        { r: sc * 0.27, tilt: -0.1, speed: 0.0002, w: 0.6, c: '165,42,58', a: 0.02, dash: [8, 18] },
        { r: sc * 0.31, tilt: 0.07, speed: -0.00012, w: 0.35, c: '251,191,36', a: 0.012, dash: [14, 28] },
        { r: sc * 0.1, tilt: 0.55, speed: 0.0007, w: 1.6, c: '165,42,58', a: 0.04 },
        { r: sc * 0.35, tilt: -0.04, speed: 0.00008, w: 0.25, c: '122,21,48', a: 0.008, dash: [6, 22] },
        { r: sc * 0.24, tilt: 0.25, speed: -0.0003, w: 0.7, c: '180,60,75', a: 0.025, dash: [4, 12] },
      ];
      rings.forEach(ring => {
        ctx.save(); ctx.translate(cx, ly);
        const rot = T * ring.speed;
        ctx.beginPath();
        for (let i = 0; i <= 80; i++) {
          const a = (i / 80) * Math.PI * 2 + rot;
          const rx = Math.cos(a) * ring.r, ry = Math.sin(a) * ring.r * Math.cos(ring.tilt);
          const rz = Math.sin(a) * ring.r * Math.sin(ring.tilt);
          const f = 400 / (400 + rz);
          i === 0 ? ctx.moveTo(rx * f, ry * f) : ctx.lineTo(rx * f, ry * f);
        }
        ctx.strokeStyle = `rgba(${ring.c},${ring.a + Math.sin(T * 0.001) * 0.008})`;
        ctx.lineWidth = ring.w;
        if (ring.dash) ctx.setLineDash(ring.dash); else ctx.setLineDash([]);
        ctx.stroke(); ctx.restore();
      });

      // Orbs with trails
      const orbs = [
        { ri: 0, speed: 0.0009, size: 5, tc: '165,42,58', c: '#a52a3a' },
        { ri: 1, speed: -0.0007, size: 4, tc: '251,191,36', c: '#fbbf24' },
        { ri: 2, speed: 0.001, size: 4, tc: '122,21,48', c: '#7a1530' },
        { ri: 5, speed: 0.0013, size: 5, tc: '165,42,58', c: '#a52a3a' },
        { ri: 3, speed: -0.00035, size: 3, tc: '251,191,36', c: '#fbbf24' },
        { ri: 7, speed: 0.0006, size: 3.5, tc: '251,191,36', c: '#fbbf24' },
      ];
      orbs.forEach(ob => {
        const ring = rings[ob.ri]; const a = T * ob.speed;
        for (let i = 1; i <= 12; i++) {
          const ta = a - i * 0.025 * Math.sign(ob.speed);
          const trx = Math.cos(ta) * ring.r, try_ = Math.sin(ta) * ring.r * Math.cos(ring.tilt);
          const trz = Math.sin(ta) * ring.r * Math.sin(ring.tilt);
          const f = 400 / (400 + trz);
          ctx.beginPath(); ctx.arc(cx + trx * f, ly + try_ * f, ob.size * (1 - i / 13), 0, 6.28);
          ctx.fillStyle = `rgba(${ob.tc},${0.1 * (1 - i / 12)})`; ctx.fill();
        }
        const ox = Math.cos(a) * ring.r, oy = Math.sin(a) * ring.r * Math.cos(ring.tilt);
        const oz = Math.sin(a) * ring.r * Math.sin(ring.tilt);
        const f = 400 / (400 + oz), px = cx + ox * f, py = ly + oy * f;
        const g = ctx.createRadialGradient(px, py, 0, px, py, ob.size * 6);
        g.addColorStop(0, `rgba(${ob.tc},0.3)`); g.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(px, py, ob.size * 6, 0, 6.28); ctx.fillStyle = g; ctx.fill();
        ctx.beginPath(); ctx.arc(px, py, ob.size, 0, 6.28); ctx.fillStyle = ob.c; ctx.fill();
      });

      // Icosahedron
      const phi = (1 + Math.sqrt(5)) / 2;
      const verts = [[-1,phi,0],[1,phi,0],[-1,-phi,0],[1,-phi,0],[0,-1,phi],[0,1,phi],[0,-1,-phi],[0,1,-phi],[phi,0,-1],[phi,0,1],[-phi,0,-1],[-phi,0,1]];
      const edges = [[0,1],[0,5],[0,7],[0,10],[0,11],[1,5],[1,7],[1,8],[1,9],[2,3],[2,4],[2,6],[2,10],[2,11],[3,4],[3,6],[3,8],[3,9],[4,5],[4,9],[4,11],[5,9],[5,11],[6,7],[6,8],[6,10],[7,8],[7,10],[8,9],[10,11]];
      const isc = sc * 0.06, rX = T * 0.0003, rY = T * 0.0005, rZ = T * 0.0002;
      function rotV(v) {
        let [x, y, z] = v;
        let y1 = y * Math.cos(rX) - z * Math.sin(rX), z1 = y * Math.sin(rX) + z * Math.cos(rX);
        let x1 = x * Math.cos(rY) + z1 * Math.sin(rY), z2 = -x * Math.sin(rY) + z1 * Math.cos(rY);
        let x2 = x1 * Math.cos(rZ) - y1 * Math.sin(rZ), y2 = x1 * Math.sin(rZ) + y1 * Math.cos(rZ);
        const f = 300 / (300 + z2 * 30);
        return { x: cx + x2 * isc * f, y: ly + y2 * isc * f, a: 0.04 + z2 * 0.01 };
      }
      ctx.lineWidth = 0.5;
      edges.forEach(e => {
        const a = rotV(verts[e[0]]), b = rotV(verts[e[1]]);
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(165,42,58,${Math.max(0, (a.a + b.a) / 2)})`; ctx.stroke();
      });

      // Lightning
      if (T % 30 < 2) spawnLightning(); // 3x more frequent
      if (T % 70 < 2 && Math.random() > 0.4) spawnLightning(); // double strikes
      for (let i = lts.length - 1; i >= 0; i--) {
        const l = lts[i]; l.life++;
        if (l.life > l.mx) { lts.splice(i, 1); continue; }
        const alpha = 1 - l.life / l.mx;
        ctx.beginPath(); ctx.moveTo(l.pts[0].x, l.pts[0].y);
        for (let j = 1; j < l.pts.length; j++) ctx.lineTo(l.pts[j].x + (Math.random() - 0.5) * 3, l.pts[j].y + (Math.random() - 0.5) * 3);
        ctx.strokeStyle = `rgba(${l.c},${alpha * 0.2})`; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.beginPath(); ctx.moveTo(l.pts[0].x, l.pts[0].y);
        for (let j = 1; j < l.pts.length; j++) ctx.lineTo(l.pts[j].x + (Math.random() - 0.5) * 1, l.pts[j].y + (Math.random() - 0.5) * 1);
        ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.08})`; ctx.lineWidth = 0.5; ctx.stroke();
      }

      // Bursts
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i]; b.life++;
        if (b.life > b.mx) { bursts.splice(i, 1); continue; }
        b.x += b.vx; b.y += b.vy; b.vx *= 0.97; b.vy *= 0.97;
        const alpha = 1 - b.life / b.mx;
        ctx.beginPath(); ctx.arc(b.x, b.y, b.r * alpha, 0, 6.28);
        ctx.fillStyle = `rgba(${b.c},${alpha * 0.45})`; ctx.fill();
      }

      requestAnimationFrame(draw);
    }
    draw();
    return () => { running = false; window.removeEventListener('resize', handleResize); };
  }, []);

  // Entrance choreography
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 200),   // flash
      setTimeout(() => setPhase(2), 600),   // logo
      setTimeout(() => setPhase(3), 2000),  // BNFT
      setTimeout(() => setPhase(4), 2800),  // NFT
      setTimeout(() => setPhase(5), 3400),  // divider
      setTimeout(() => setPhase(6), 3700),  // subtitle
      setTimeout(() => setPhase(7), 4000),  // badges
      setTimeout(() => setPhase(8), 4800),  // corners + bottom
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Pulse waves
  useEffect(() => {
    if (phase < 2) return;
    const prc = document.getElementById('hero-pulses');
    if (!prc) return;
    function pulse(color, width, duration) {
      const el = document.createElement('div');
      el.className = 'hero__pulse';
      prc.appendChild(el);
      const sz = Math.max(window.innerWidth, window.innerHeight) * 2.2;
      el.animate([
        { width: '0px', height: '0px', opacity: 0.35, borderWidth: width + 'px', borderStyle: 'solid', borderColor: color },
        { width: sz + 'px', height: sz + 'px', opacity: 0, borderWidth: '1px', borderStyle: 'solid', borderColor: 'transparent' }
      ], { duration, easing: 'cubic-bezier(0.22,1,0.36,1)' }).onfinish = () => el.remove();
    }
    pulse('rgba(165,42,58,0.3)', 3, 2500);
    const iv1 = setInterval(() => pulse('rgba(165,42,58,0.1)', 2, 3000), 6000);
    const iv2 = setInterval(() => pulse('rgba(251,191,36,0.07)', 1.5, 3500), 8000);
    return () => { clearInterval(iv1); clearInterval(iv2); };
  }, [phase]);

  return (
    <div className="hero">
      <canvas ref={canvasRef} className="hero__canvas" />
      <div className="hero__pulses" id="hero-pulses" />

      {/* Flash */}
      <div className={`hero__flash ${phase >= 1 ? 'fire' : ''}`} />

      {/* Floating hex nodes */}
      <div className={`hero__nodes ${phase >= 8 ? 'show' : ''}`}>
        {[
          { label: 'BCOIN', x: '5%', y: '8%', size: 50, color: 'cyan', delay: '0s', dur: '16s' },
          { label: 'USDT', x: '88%', y: '12%', size: 55, color: 'gold', delay: '-5s', dur: '19s' },
          { label: 'BLUR', x: '3%', y: '75%', size: 42, color: 'purple', delay: '-8s', dur: '21s' },
          { label: 'ARCADE', x: '90%', y: '70%', size: 45, color: 'cyan', delay: '-11s', dur: '17s' },
          { label: '', x: '2%', y: '42%', size: 35, color: 'gold', delay: '-3s', dur: '22s' },
          { label: '', x: '95%', y: '40%', size: 38, color: 'purple', delay: '-14s', dur: '20s' },
        ].map((node, i) => (
          <div key={i} className={`hero__hex hero__hex--${node.color}`}
               style={{ left: node.x, top: node.y, animationDelay: node.delay, animationDuration: node.dur }}>
            <svg width={node.size} height={node.size * 0.87} viewBox="0 0 60 52">
              <path d="M30 1L56 14V38L30 51 4 38V14Z" fill="none" strokeWidth="0.7" />
              {node.label && <text x="30" y="29" textAnchor="middle" fontSize="5" fontFamily="Orbitron" fontWeight="700">{node.label}</text>}
            </svg>
          </div>
        ))}
      </div>

      {/* Corner frames */}
      {['tl', 'tr', 'bl', 'br'].map(pos => (
        <div key={pos} className={`hero__corner hero__corner--${pos} ${phase >= 8 ? 'show' : ''}`}>
          <svg viewBox="0 0 100 100" fill="none">
            <path d="M0 45V2H2V0H45" stroke={pos.includes('b') ? 'rgba(251,191,36,0.5)' : 'rgba(165,42,58,0.5)'} strokeWidth="1" />
            <circle cx="2" cy="2" r="2.5" fill={pos.includes('b') ? 'rgba(251,191,36,0.4)' : 'rgba(165,42,58,0.4)'}>
              <animate attributeName="r" values="2;3.5;2" dur="3s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      ))}

      {/* CONTENT */}
      <div className="hero__content">
        {/* Logo */}
        <div className={`hero__logo ${phase >= 2 ? 'show' : ''}`}>
          <div className="hero__logo-glow" />
          <div className="hero__logo-orbit hero__logo-orbit--1" />
          <div className="hero__logo-orbit hero__logo-orbit--2" />
          <div className="hero__logo-orbit hero__logo-orbit--3" />
          <img src="/logo.png" alt="BNFT" className="hero__logo-img" />
        </div>

        {/* Title */}
        <div className="hero__title-wrap">
          <h1 className={`hero__t1 notranslate ${phase >= 3 ? 'show' : ''}`}>BNFT</h1>
          <div className={`hero__t1-ghost hero__t1-ghost--r ${phase >= 3 ? 'show' : ''}`}>BNFT</div>
          <div className={`hero__t1-ghost hero__t1-ghost--b ${phase >= 3 ? 'show' : ''}`}>BNFT</div>
          <div className={`hero__glitch ${phase >= 3 ? 'fire' : ''}`} />
        </div>
        <h2 className={`hero__t2 ${phase >= 4 ? 'show' : ''}`}></h2>
        <div className={`hero__divider ${phase >= 5 ? 'show' : ''}`} />
        <p className={`hero__sub ${phase >= 6 ? 'show' : ''}`}>The Future of Decentralized NFT Earning</p>

        <div className="hero__badges">
          {[
            { icon: '⛓', label: 'BEP-20 / BSC', color: '#fbbf24', delay: 0 },
            { icon: '◈', label: '10% BCoin + 90% USDT', color: '#fde68a', delay: 200 },
            { icon: '◆', label: 'BLUR × ARCADE Partners', color: '#a52a3a', delay: 400 },
          ].map((b, i) => (
            <div key={i} className={`hero__badge ${phase >= 7 ? 'show' : ''}`}
                 style={{ transitionDelay: b.delay + 'ms' }}>
              <span style={{ color: b.color, fontSize: '1.2em' }}>{b.icon}</span> {b.label}
            </div>
          ))}
        </div>

        <a href="https://bnft.live/" target="_blank" rel="noopener noreferrer"
           className={`hero__web ${phase >= 7 ? 'show' : ''}`}>
          ◆ bnft.live ◆
        </a>
      </div>

      {/* Slide label */}
      <div className={`hero__label ${phase >= 8 ? 'show' : ''}`}>
        <span>{String(slideNumber).padStart(2, '0')} / {String(totalSlides).padStart(2, '0')}</span>
      </div>
    </div>
  );
}
